/**
 * CopilotActionProvider
 *
 * Wraps the app (inside MainLayout). Provides ActionBridge state via React
 * context, wires the router's navigate function, and mounts window.sgrcCopilot.
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  executeAction,
  injectNavigate,
  mountGlobalBridge,
  readPageContext,
  type UiAction,
  type ActionResult,
  type PageContext,
} from './action-bridge'
import { mountToolExecutorBridge } from './tool-executor'
import { mountToolRegistryBridge } from './tool-registry'

// ── Context value ─────────────────────────────────────────────────────────────

export interface CopilotActionContextValue {
  execute: (action: UiAction) => Promise<ActionResult>
  readContext: () => PageContext
}

const CopilotActionContext = createContext<CopilotActionContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────

interface CopilotActionProviderProps {
  children: ReactNode
}

export function CopilotActionProvider({ children }: CopilotActionProviderProps) {
  const navigate = useNavigate()

  // Wire the router into the bridge singleton on every render cycle where
  // navigate identity may have changed (router v6 guarantees stability, but
  // keeping it in useEffect is the safe pattern).
  useEffect(() => {
    injectNavigate(navigate)
    mountGlobalBridge()
    mountToolExecutorBridge()
    // Unified Companion tool registry (subsystem C) — importing tool-registry
    // also primes the agent-runner's read/write side-effect cache at module load.
    mountToolRegistryBridge()
  }, [navigate])

  const value: CopilotActionContextValue = {
    execute: executeAction,
    readContext: readPageContext,
  }

  return (
    <CopilotActionContext.Provider value={value}>
      {children}
    </CopilotActionContext.Provider>
  )
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useCopilotAction(): CopilotActionContextValue {
  const ctx = useContext(CopilotActionContext)
  if (!ctx) {
    throw new Error('useCopilotAction must be used inside <CopilotActionProvider>')
  }
  return ctx
}
